import { useState } from 'react'
import * as m from '~/paraglide/messages'
import type { EventPrice } from '~/shared/types/event'
import { Button } from '~/shared/ui/button'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'

interface EventPriceSelectorProps {
  value: EventPrice
  onChange: (price: EventPrice) => void
  error?: string
  onBlur?: () => void
}

function parsePrice(input: string): number {
  if (!input) return 0
  const parsed = Number.parseFloat(input)
  return Number.isNaN(parsed) ? 0 : Math.max(0, parsed)
}

export function EventPriceSelector({ value, onChange, error, onBlur }: EventPriceSelectorProps) {
  const [touched, setTouched] = useState(false)

  const handleBlur = () => {
    setTouched(true)
    onBlur?.()
  }

  const handleKindChange = (kind: EventPrice['kind']) => {
    setTouched(true)
    if (kind === 'unknown') {
      onChange({ kind: 'unknown' })
    } else if (kind === 'free') {
      onChange({ kind: 'free' })
    } else if (kind === 'fixed') {
      onChange({ kind: 'fixed', priceFixed: 0 })
    } else if (kind === 'range') {
      onChange({ kind: 'range', priceRangeMin: 0, priceRangeMax: 0 })
    }
  }

  const handleFixedPriceChange = (input: string) => {
    onChange({ kind: 'fixed', priceFixed: parsePrice(input) })
  }

  const handleRangeMinChange = (input: string) => {
    if (value.kind === 'range') {
      onChange({
        kind: 'range',
        priceRangeMin: parsePrice(input),
        priceRangeMax: value.priceRangeMax,
      })
    }
  }

  const handleRangeMaxChange = (input: string) => {
    if (value.kind === 'range') {
      onChange({
        kind: 'range',
        priceRangeMin: value.priceRangeMin,
        priceRangeMax: parsePrice(input),
      })
    }
  }

  const shouldShowError = touched && error

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{m.pricing()}</Label>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant={value.kind === 'free' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleKindChange('free')}
          >
            {m.free()}
          </Button>
          <Button
            type="button"
            variant={value.kind === 'fixed' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleKindChange('fixed')}
          >
            {m.fixedPrice()}
          </Button>
          <Button
            type="button"
            variant={value.kind === 'range' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleKindChange('range')}
          >
            {m.priceRange()}
          </Button>
          <Button
            type="button"
            variant={value.kind === 'unknown' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleKindChange('unknown')}
          >
            {m.priceUnknown()}
          </Button>
        </div>
      </div>

      {value.kind === 'fixed' && (
        <div className="space-y-2">
          <Label>{m.enterPrice()}</Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              €
            </span>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={value.priceFixed ?? ''}
              onChange={(e) => handleFixedPriceChange(e.target.value)}
              onBlur={handleBlur}
              className="pl-7"
              placeholder="0.00"
            />
          </div>
        </div>
      )}

      {value.kind === 'range' && (
        <div className="grid grid-cols-1 gap-4 xs:grid-cols-2">
          <div className="space-y-2">
            <Label>{m.minPrice()}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                €
              </span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={value.priceRangeMin ?? ''}
                onChange={(e) => handleRangeMinChange(e.target.value)}
                onBlur={handleBlur}
                className="pl-7"
                placeholder="0.00"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{m.maxPrice()}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                €
              </span>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={value.priceRangeMax ?? ''}
                onChange={(e) => handleRangeMaxChange(e.target.value)}
                onBlur={handleBlur}
                className="pl-7"
                placeholder="0.00"
              />
            </div>
          </div>
        </div>
      )}

      {shouldShowError && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}
